import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { UserCog } from "lucide-react";

/**
 * Login page for STAFF / sub-admin accounts issued by the main admin.
 * Reuses /api/auth/login but restricts entry to role=staff (admins should
 * use /admin/login).
 */
export default function ManagementLogin() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user?.role === "admin" || user?.role === "staff") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "staff" && u.role !== "admin") {
        toast.error("Not a management account. Parents please use the parent login.");
        return;
      }
      if (u.role === "admin") {
        toast.info("Admin account detected — redirecting to admin console");
      } else {
        toast.success(`Welcome, ${u.name?.split(" ")[0] || "staff"}`);
      }
      nav("/admin");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16 bg-[#0B1120] text-white" data-testid="management-login">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 border border-brand-blue/50 flex items-center justify-center brand-blue">
            <UserCog size={18} />
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-blue">// Staff · Management</div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-none tracking-tighter">
          Team<br /><span className="brand-blue">console</span>.
        </h1>
        <p className="text-sm text-white/60 mt-3">
          Enter your staff credentials issued by the HTP Sports Hub admin office.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-4">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Staff email" required
            data-testid="mgmt-email"
            className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" required
            data-testid="mgmt-password"
            className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <button
            type="submit" disabled={loading}
            data-testid="mgmt-submit"
            className="w-full bg-brand-blue text-white py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-50"
          >
            {loading ? "…" : "Enter Console"}
          </button>
        </form>

        <div className="mt-6 text-xs font-mono text-white/40 text-center space-y-1">
          <div>Not a staff member?</div>
          <div className="flex justify-center gap-4">
            <Link to="/parent/login" className="brand-blue hover:underline">Parent login</Link>
            <span>·</span>
            <Link to="/admin/login" className="brand-orange hover:underline">Main admin</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
